import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { publicAPI } from '../../services/api';

// Extend Window interface to include TikTok Pixel global
declare global {
  interface Window {
    ttq: any;
    TiktokAnalyticsObject: string;
  }
}

/**
 * TikTokPixel component
 *
 * Dynamically fetches the active TikTok Pixel ID from the backend,
 * injects the TikTok pixel script into <head>, and fires a PageView
 * on every route change — mirroring the FacebookPixel component pattern.
 *
 * Renders nothing to the DOM (returns null).
 */
const TikTokPixel: React.FC = () => {
  const location = useLocation();
  const [pixelId, setPixelId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // ── Step 1: Fetch active pixel ID from backend ──────────────────────────
  useEffect(() => {
    const fetchPixelId = async () => {
      // Serve from localStorage first for instant load (no FOUC)
      const cachedId = localStorage.getItem('tt_pixel_id');
      if (cachedId) {
        setPixelId(cachedId);
      }

      try {
        const data = await publicAPI.getActiveTikTokPixel();
        if (data && data.pixel_id) {
          // Only update state + cache if different (avoids re-init)
          if (data.pixel_id !== cachedId) {
            setPixelId(data.pixel_id);
            localStorage.setItem('tt_pixel_id', data.pixel_id);
          }
        } else {
          // No active pixel configured — clear old cache
          localStorage.removeItem('tt_pixel_id');
        }
      } catch (error) {
        // Fail silently — pixel not critical to user experience
        console.error('TikTok Pixel: Failed to fetch Pixel ID from backend:', error);
      }
    };

    fetchPixelId();
  }, []);

  // ── Step 2: Initialize TikTok Pixel when ID is available ────────────────
  useEffect(() => {
    if (!pixelId || initialized) return;

    if (!window.ttq) {
      // Inject the TikTok Pixel base code into <head>
      const script = document.createElement('script');
      script.innerHTML = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;
          var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){
            for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);
            return e
          },
          ttq.load=function(e,n){
            var i="https://analytics.tiktok.com/i18n/pixel/events.js";
            ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,
            ttq._t=ttq._t||{},ttq._t[e]=+new Date,
            ttq._o=ttq._o||{},ttq._o[e]=n||{};
            n=document.createElement("script");
            n.type="text/javascript",n.async=!0,
            n.src=i+"?sdkid="+e+"&lib="+t;
            e=document.getElementsByTagName("script")[0];
            e.parentNode.insertBefore(n,e)
          };
          ttq.load('${pixelId}');
          ttq.page();
        }(window, document, 'ttq');
      `;
      document.head.appendChild(script);
      setInitialized(true);
    } else {
      // Script already injected (e.g., HMR reload) — just load pixel
      window.ttq.load(pixelId);
      setInitialized(true);
    }
  }, [pixelId, initialized]);

  // ── Step 3: Fire PageView on every route change ──────────────────────────
  useEffect(() => {
    if (initialized && window.ttq) {
      window.ttq.page();
    }
  }, [location, initialized]);

  return null; // Component renders nothing to the DOM
};

export default TikTokPixel;
